import { useMemo } from 'react'
import { useQueries } from '@tanstack/react-query'
import { apiGet } from '@/api/main/client'
import { PaymentRequisiteModel, PaymentRequisiteStatisticModel } from '../model'
import { PaginatedCollection } from '@/api/types'
import { requisiteStatisticApi } from '@/features/requisites'

export type EnrichedPaymentRequisiteModel = PaymentRequisiteModel & {
  totalStats?: PaymentRequisiteStatisticModel
  dailyStats?: PaymentRequisiteStatisticModel
}

export const useEnrichedPaymentRequisites = (
  requisites?: PaymentRequisiteModel[] | null
): {
  enriched: EnrichedPaymentRequisiteModel[]
  isLoading: boolean
  isError: boolean
  isSuccess: boolean
} => {
  const currentDate = new Date().toISOString().split('T')[0] + 'T00:00:00.000Z'
  const ids = useMemo(() => requisites?.map((r) => r.id) ?? [], [requisites])
  const queryString = ids.map((id) => `ids=${encodeURIComponent(id)}`).join('&')

  const queries = useQueries({
    queries: [
      {
        queryKey: ['payment-req-total-stat', ids],
        queryFn: () =>
          apiGet<PaginatedCollection<PaymentRequisiteStatisticModel[]>>(
            requisiteStatisticApi.getPaymentReqTotal(queryString)
          ),
        enabled: !!ids.length,
      },
      {
        queryKey: ['payment-req-daily-stat', ids, currentDate],
        queryFn: () =>
          apiGet<PaginatedCollection<PaymentRequisiteStatisticModel[]>>(
            requisiteStatisticApi.getPaymentReqDaily(queryString),
            { date: currentDate }
          ),
        enabled: !!ids.length,
      },
    ],
  })

  const totalStatList = queries[0]?.data?.items ?? []
  const dailyStatList = queries[1]?.data?.items ?? []

  const enriched = useMemo(() => {
    if (!requisites?.length) return []

    return requisites.map((req) => {
      const totalStats = totalStatList.find((stat) => stat.paymentRequisitId === req.id)
      const dailyStats = dailyStatList.find((stat) => stat.paymentRequisitId === req.id)

      return {
        ...req,
        totalStats,
        dailyStats,
      }
    })
  }, [requisites, totalStatList, dailyStatList])

  const isLoading = queries.some((q) => q.isLoading)
  const isError = queries.some((q) => q.isError)
  const isSuccess = queries.every((q) => q.isSuccess)

  return {
    enriched,
    isLoading,
    isError,
    isSuccess,
  }
}
